import { Fragment, memo, useCallback, useEffect } from 'react';

import PropTypes from 'prop-types';
import { useAnalytics } from 'use-analytics';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import Dropdown from '../../../Dropdown';
import Filter from '../../../Filters/Filter';
import RangeSetting from '../../../RangeSetting';
import StateroomFilling from '../StateroomFilling';
import { defaultStateroomFilling, Staterooms, Rooms, SearchModes } from '../../../../constants';
import formatStateroomsInput from '../../../../helpers/formatStateroomsInput';
import { setTripSearchInputs } from '../../../../store/slices/tripSearch';

const PassengersFilter = ({
  variant,
  disabled,
  doHighlightOnChange,
  filterMaxWidthClass,
  bgClassName,
}) => {
  const MAX_STATEROOMS = 3;
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const storeStaterooms = useSelector((store) => store.tripSearch.searchInputs.staterooms || []);
  const defaultStaterooms = useSelector((store) => store.tripSearch.searchObj.staterooms || []);
  const { selectedSearchMode } = useSelector((store) => store.tripSearch);
  const { travelAgency } = useSelector((store) => store.user);
  // if disabled set, show staterooms from store.tripSearch.searchObj not
  // store.tripSearch.searchInputs because we don't want to allow update stateroom value
  const staterooms = disabled ? defaultStaterooms : storeStaterooms;
  const isPassengersEmpty = doHighlightOnChange
    ? disabled ||
      ((staterooms || []).length === 1 &&
        staterooms[0].adults === defaultStateroomFilling.adults &&
        staterooms[0].seniors === defaultStateroomFilling.seniors &&
        staterooms[0].lapInfants === defaultStateroomFilling.lapInfants &&
        staterooms[0].seatInfants === defaultStateroomFilling.seatInfants &&
        staterooms[0].children.length === 0)
    : true;

  const getLabel = useCallback(
    () =>
      formatStateroomsInput(
        staterooms || [],
        travelAgency && travelAgency.enabledTransportModes,
        selectedSearchMode,
      ),
    [staterooms, travelAgency, selectedSearchMode],
  );

  const handleClearFilter = useCallback(() => {
    dispatch(
      setTripSearchInputs({
        staterooms: [defaultStateroomFilling],
      }),
    );
    track('filterBar.clearFilter');
  }, [dispatch, track]);

  const handleChangeFilter = useCallback(
    (filterValue) => {
      dispatch(
        setTripSearchInputs({
          ...filterValue,
        }),
      );
      track('filterBar.clearFilter');
    },
    [dispatch, track],
  );

  const incrementStateroom = () => {
    if (staterooms.length >= MAX_STATEROOMS) {
      return;
    }
    const updatedStaterooms = [...staterooms];
    updatedStaterooms.push(defaultStateroomFilling);
    dispatch(
      setTripSearchInputs({
        staterooms: updatedStaterooms,
      }),
    );

    track('filterBar.incrementStaterooms');
  };

  const decrementStateroom = () => {
    if (staterooms.length <= 1) {
      return;
    }
    const updatedStaterooms = [...staterooms];
    updatedStaterooms.pop();
    dispatch(
      setTripSearchInputs({
        staterooms: updatedStaterooms,
      }),
    );
    track('filterBar.decrementStateroom');
  };
  const enableBookingForMinors =
    travelAgency && travelAgency.settings && travelAgency.settings.enableBookingForMinors;
  useEffect(() => {
    if (enableBookingForMinors && staterooms && selectedSearchMode !== SearchModes.FLIGHT) {
      staterooms.forEach((room, index) => {
        if (room.adults + room.seniors === 0) {
          const updatedStaterooms = [...staterooms];
          updatedStaterooms[index] = {
            ...defaultStateroomFilling,
          };
          handleChangeFilter({ staterooms: updatedStaterooms });
        }
      });
    }
  }, [enableBookingForMinors, handleChangeFilter, selectedSearchMode, staterooms]);

  return (
    <Filter
      filterMaxWidthClass={filterMaxWidthClass}
      label={getLabel()}
      menuWidthClass="w-max"
      isFilterEmpty={isPassengersEmpty}
      handleClear={handleClearFilter}
      disableCloseOnOutsideClick
      variant={variant}
      bgClassName={bgClassName}
    >
      <Dropdown.Item>
        <div className="grid grid-cols-3 w-full items-center col-span-5 gap-3">
          {selectedSearchMode === SearchModes.CRUISE_TRIP && (
            <RangeSetting
              key={selectedSearchMode === SearchModes.CRUISE_TRIP ? Staterooms.type : Rooms.type}
              type={selectedSearchMode === SearchModes.CRUISE_TRIP ? Staterooms.type : Rooms.type}
              label={
                selectedSearchMode === SearchModes.CRUISE_TRIP ? Staterooms.label : Rooms.label
              }
              helperText={Staterooms.helperText}
              onIncrement={disabled ? null : incrementStateroom}
              onDecrement={disabled ? null : decrementStateroom}
              value={staterooms && staterooms.length}
            />
          )}
          {staterooms.map((_room, index) => (
            <Fragment key={`${index.toString()}`}>
              {staterooms && staterooms.length > 1 && (
                <div
                  className={clsx(
                    'col-span-3 rounded px-2 py-1',
                    { 'bg-blue-200 text-white text-theme font-bold': variant !== 'outline' },
                    { 'bg-white text-theme border-2 font-medium': variant === 'outline' },
                  )}
                >
                  Room {index + 1}
                </div>
              )}
              <StateroomFilling
                staterooms={staterooms}
                onChangeFilter={handleChangeFilter}
                roomIndex={index}
                key={`${index.toString()}`}
                disabled={disabled}
              />
            </Fragment>
          ))}
        </div>
      </Dropdown.Item>
    </Filter>
  );
};

PassengersFilter.propTypes = {
  variant: PropTypes.string,
  disabled: PropTypes.bool,
  doHighlightOnChange: PropTypes.bool,
  filterMaxWidthClass: PropTypes.string,
  bgClassName: PropTypes.string,
};

PassengersFilter.defaultProps = {
  variant: 'standard',
  disabled: false,
  doHighlightOnChange: true,
  filterMaxWidthClass: 'max-w-full',
  bgClassName: undefined,
};

export default memo(PassengersFilter);
